import { z } from 'zod'

export const localeSchema = z.enum(['zh-Hant', 'en'])
export type Locale = z.infer<typeof localeSchema>

export const localizedTextSchema = z.object({
  'zh-Hant': z.string().min(1),
  en: z.string().min(1),
})
export type LocalizedText = z.infer<typeof localizedTextSchema>

export const datasetIdSchema = z.string().regex(/^[a-z0-9-]+$/)
export type DatasetId = z.infer<typeof datasetIdSchema>

export const traditionAccentSchema = z.object({
  accent: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  accentSoft: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  accentMuted: z.string().min(1),
  panelTint: z.string().min(1),
})
export type TraditionAccent = z.infer<typeof traditionAccentSchema>

export const datasetCatalogueEntrySchema = z.object({
  id: datasetIdSchema,
  dataset: z.string().min(1),
  slug: z.string().regex(/^[a-z0-9-]+$/),
  name: localizedTextSchema,
  shortName: localizedTextSchema,
  description: localizedTextSchema,
  status: z.enum(['available', 'coming-soon']),
  defaultStoryId: z.string().regex(/^[a-z0-9-]+$/).optional(),
  manifestPath: z.string().min(1),
  bootstrapPath: z.string().min(1),
  accent: traditionAccentSchema,
})
export type DatasetCatalogueEntry = z.infer<typeof datasetCatalogueEntrySchema>

export const datasetCatalogueSchema = z.object({
  schemaVersion: z.literal(1),
  defaultDatasetId: datasetIdSchema,
  datasets: z.array(datasetCatalogueEntrySchema).min(1),
}).superRefine((catalogue, context) => {
  const ids = new Set<string>()
  catalogue.datasets.forEach((entry, index) => {
    if (ids.has(entry.id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['datasets', index, 'id'],
        message: `Duplicate dataset id: ${entry.id}`,
      })
    }
    ids.add(entry.id)
  })
  if (!ids.has(catalogue.defaultDatasetId)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['defaultDatasetId'],
      message: `Default dataset is missing: ${catalogue.defaultDatasetId}`,
    })
  }
})
export type DatasetCatalogue = z.infer<typeof datasetCatalogueSchema>

export const nodeTypeSchema = z.enum(['deity', 'hero', 'mortal', 'creature', 'place', 'event', 'artifact'])
export type NodeType = z.infer<typeof nodeTypeSchema>

export const edgeTypeSchema = z.enum([
  'family',
  'help',
  'conflict',
  'love',
  'participation',
  'geography',
  'causality',
  'creation',
  'possession',
])
export type EdgeType = z.infer<typeof edgeTypeSchema>

export const relationshipClassSchema = z.enum(['factual', 'contextual', 'causal', 'disputed'])
export type RelationshipClass = z.infer<typeof relationshipClassSchema>

export const relationshipScopeSchema = z.enum(['stable', 'story', 'event', 'variant'])
export type RelationshipScope = z.infer<typeof relationshipScopeSchema>

export const explanationDecisionSchema = z.enum(['required', 'not-required', 'deferred-with-reason'])
export type ExplanationDecision = z.infer<typeof explanationDecisionSchema>

export const mediaKindSchema = z.enum(['ai-generated', 'original', 'public-domain', 'creative-commons', 'licensed', 'tmdb'])
export type MediaKind = z.infer<typeof mediaKindSchema>

export const mediaAssetSchema = z.object({
  src: z.string().min(1),
  kind: mediaKindSchema,
  alt: localizedTextSchema,
  credit: z.string().min(1),
  license: z.string().min(1),
  sourceUrl: z.string().url(),
})
export type MediaAsset = z.infer<typeof mediaAssetSchema>

export const historicalMediaAssetSchema = mediaAssetSchema.extend({
  date: localizedTextSchema.optional(),
  medium: localizedTextSchema.optional(),
  institution: localizedTextSchema.optional(),
  modificationNote: localizedTextSchema.optional(),
})
export type HistoricalMediaAsset = z.infer<typeof historicalMediaAssetSchema>

export const nodeMediaSetSchema = z.object({
  graphPortrait: mediaAssetSchema.optional(),
  inspectorHero: mediaAssetSchema.optional(),
  historicalRepresentations: z.array(historicalMediaAssetSchema).default([]),
})
export type NodeMediaSet = z.infer<typeof nodeMediaSetSchema>

export const contentStatusSchema = z.enum(['stub', 'draft', 'reviewed'])
export type ContentStatus = z.infer<typeof contentStatusSchema>

export const artifactTypeSchema = z.enum([
  'weapon',
  'vehicle',
  'garment',
  'container',
  'ritual-object',
  'quest-object',
  'constructed-device',
  'other',
])
export type ArtifactType = z.infer<typeof artifactTypeSchema>

export const artifactProfileSchema = z.object({
  artifactType: artifactTypeSchema,
  originSummary: localizedTextSchema,
})
export type ArtifactProfile = z.infer<typeof artifactProfileSchema>

export const locationStatusSchema = z.enum([
  'historically-identifiable',
  'traditional-association',
  'approximate',
  'disputed',
  'mythic-realm',
  'unlocatable',
])
export type LocationStatus = z.infer<typeof locationStatusSchema>

export const factBlockSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  title: localizedTextSchema,
  body: localizedTextSchema,
  sourceRefs: z.array(z.string()).min(1),
  variantLabel: localizedTextSchema.optional(),
  spoilerLevel: z.number().int().min(0).max(2),
})
export type FactBlock = z.infer<typeof factBlockSchema>

export const entityProfileSchema = z.object({
  originalName: z.string().min(1).optional(),
  transliteration: z.string().min(1).optional(),
  epithets: z.array(localizedTextSchema).default([]),
  domains: z.array(localizedTextSchema).default([]),
  symbols: z.array(localizedTextSchema).default([]),
  overview: localizedTextSchema,
  factBlocks: z.array(factBlockSchema).min(1),
})
export type EntityProfile = z.infer<typeof entityProfileSchema>

export const entityProfileRecordSchema = z.object({
  entityId: z.string(),
  contentStatus: contentStatusSchema,
  profile: entityProfileSchema,
})
export type EntityProfileRecord = z.infer<typeof entityProfileRecordSchema>

export const adaptationSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  title: localizedTextSchema,
  year: z.number().int().min(1880).max(2200),
  format: z.enum(['film', 'tv', 'miniseries', 'stage', 'game']),
  characterName: localizedTextSchema,
  performer: z.string().min(1).optional(),
  externalUrl: z.string().url(),
  media: mediaAssetSchema.optional(),
})
export type Adaptation = z.infer<typeof adaptationSchema>

export const graphNodeSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  type: nodeTypeSchema,
  name: localizedTextSchema,
  summary: localizedTextSchema,
  aliases: z.array(z.string()).default([]),
  sourceRefs: z.array(z.string()).min(1),
  spoilerLevel: z.number().int().min(0).max(2).default(0),
  featured: z.boolean().default(false),
  media: mediaAssetSchema.optional(),
  mediaSet: nodeMediaSetSchema.optional(),
  contentStatus: contentStatusSchema.default('stub'),
  profile: entityProfileSchema.optional(),
  artifactProfile: artifactProfileSchema.optional(),
  locationStatus: locationStatusSchema.optional(),
  adaptations: z.array(adaptationSchema).default([]),
})
export type GraphNode = z.infer<typeof graphNodeSchema>

export const relationshipExplanationSchema = z.object({
  kind: z.enum(['factual', 'contextual', 'causal']),
  why: localizedTextSchema.optional(),
  result: localizedTextSchema.optional(),
}).refine((value) => value.why || value.result, {
  message: 'Relationship explanation requires why or result',
})
export type RelationshipExplanation = z.infer<typeof relationshipExplanationSchema>

export const relationshipExplanationGraphSchema = z.object({
  nodeIds: z.array(z.string()).min(1),
  edgeIds: z.array(z.string()).min(1),
})
export type RelationshipExplanationGraph = z.infer<typeof relationshipExplanationGraphSchema>

export const edgeContextSchema = z.object({
  storyIds: z.array(z.string()).default([]),
  eventIds: z.array(z.string()).default([]),
  stepIds: z.array(z.string()).default([]),
  sourceRefs: z.array(z.string()).default([]),
  variantLabel: localizedTextSchema.optional(),
  relationshipClass: relationshipClassSchema.optional(),
  explanationDecision: explanationDecisionSchema.optional(),
  deferredReason: localizedTextSchema.optional(),
  explanation: relationshipExplanationSchema.optional(),
  explanationGraph: relationshipExplanationGraphSchema.optional(),
})
export type EdgeContext = z.infer<typeof edgeContextSchema>

export const graphEdgeSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  source: z.string(),
  target: z.string(),
  type: edgeTypeSchema,
  directed: z.boolean().default(true),
  label: localizedTextSchema,
  summary: localizedTextSchema,
  sourceRefs: z.array(z.string()).min(1),
  relationshipScope: relationshipScopeSchema,
  storyRefs: z.array(z.string()).default([]),
  contexts: z.array(edgeContextSchema).default([]),
  featured: z.boolean().default(false),
  spoilerLevel: z.number().int().min(0).max(2).default(0),
})
export type GraphEdge = z.infer<typeof graphEdgeSchema>

export const sourceRefSchema = z.object({
  id: z.string(),
  author: localizedTextSchema,
  work: localizedTextSchema,
  locator: localizedTextSchema,
  url: z.string().url(),
  license: z.string().min(1),
})
export type SourceRef = z.infer<typeof sourceRefSchema>

export const storyStepSchema = z.object({
  id: z.string(),
  order: z.number().int().positive(),
  title: localizedTextSchema,
  summary: localizedTextSchema,
  narrative: z.object({
    setup: localizedTextSchema.optional(),
    development: localizedTextSchema.optional(),
    consequence: localizedTextSchema.optional(),
  }).refine((value) => value.setup || value.development || value.consequence, {
    message: 'Story step narrative requires setup, development, or consequence',
  }).optional(),
  nodeIds: z.array(z.string()).min(1),
  edgeIds: z.array(z.string()),
  sourceRefs: z.array(z.string()).min(1),
})

export const storySchema = z.object({
  id: z.string(),
  cycle: z.string(),
  title: localizedTextSchema,
  description: localizedTextSchema,
  status: z.enum(['available', 'coming-soon']),
  steps: z.array(storyStepSchema),
})
export type Story = z.infer<typeof storySchema>

export const storyAppearanceStepContextSchema = z.object({
  stepId: z.string(),
  role: localizedTextSchema.optional(),
  setup: localizedTextSchema.optional(),
  development: localizedTextSchema.optional(),
  consequence: localizedTextSchema.optional(),
  sourceRefs: z.array(z.string()).min(1),
}).refine((value) => value.role || value.setup || value.development || value.consequence, {
  message: 'Story appearance step context requires reader-facing content',
})
export type StoryAppearanceStepContext = z.infer<typeof storyAppearanceStepContextSchema>

export const storyAppearanceSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  entityId: z.string(),
  storyId: z.string(),
  stepIds: z.array(z.string()).min(1),
  role: localizedTextSchema,
  summary: localizedTextSchema,
  stepContexts: z.array(storyAppearanceStepContextSchema).default([]),
  sourceRefs: z.array(z.string()).min(1),
  spoilerLevel: z.number().int().min(0).max(2),
})
export type StoryAppearance = z.infer<typeof storyAppearanceSchema>

export const storyCycleSchema = z.object({
  id: z.string(),
  name: localizedTextSchema,
  description: localizedTextSchema,
  storyIds: z.array(z.string()),
})
export type StoryCycle = z.infer<typeof storyCycleSchema>

export const collectionKindSchema = z.enum(['title', 'group', 'faction', 'lineage', 'theme'])
export type CollectionKind = z.infer<typeof collectionKindSchema>

export const collectionStatusSchema = z.enum(['draft', 'review', 'available'])
export type CollectionStatus = z.infer<typeof collectionStatusSchema>

export const collectionReviewDecisionSchema = z.enum(['go', 'adjust', 'stop'])
export type CollectionReviewDecision = z.infer<typeof collectionReviewDecisionSchema>

export const collectionMembershipSchema = z.object({
  nodeId: z.string().regex(/^[a-z0-9-]+$/),
  sourceRefs: z.array(z.string()).default([]),
  note: localizedTextSchema.optional(),
  review: collectionReviewDecisionSchema.optional(),
})
export type CollectionMembership = z.infer<typeof collectionMembershipSchema>

export const collectionVariantSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  label: localizedTextSchema,
  description: localizedTextSchema,
  attribution: localizedTextSchema,
  sourceRefs: z.array(z.string()).min(1),
  members: z.array(collectionMembershipSchema).min(1),
  review: collectionReviewDecisionSchema.optional(),
})
export type CollectionVariant = z.infer<typeof collectionVariantSchema>

export const collectionSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  title: localizedTextSchema,
  description: localizedTextSchema,
  kind: collectionKindSchema,
  status: collectionStatusSchema,
  inclusionPolicy: localizedTextSchema,
  exclusions: z.array(localizedTextSchema).min(1),
  defaultVariantId: z.string().regex(/^[a-z0-9-]+$/),
  variants: z.array(collectionVariantSchema).min(1),
  relatedStoryIds: z.array(z.string()).default([]),
  relatedCycleIds: z.array(z.string()).default([]),
  sourceRefs: z.array(z.string()).min(1),
  review: collectionReviewDecisionSchema.optional(),
})
export type Collection = z.infer<typeof collectionSchema>

export const timelineTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('node'), nodeId: z.string().regex(/^[a-z0-9-]+$/) }),
  z.object({ kind: z.literal('edge'), edgeId: z.string().regex(/^[a-z0-9-]+$/) }),
])
export type TimelineTarget = z.infer<typeof timelineTargetSchema>

export const timelineTickSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  order: z.number().int().positive(),
  target: timelineTargetSchema,
  mode: z.enum(['introduce', 'revisit']),
  storyId: z.string().optional(),
  stepId: z.string().optional(),
  chronologyNote: localizedTextSchema.optional(),
  captionOverride: localizedTextSchema.optional(),
})
export type TimelineTick = z.infer<typeof timelineTickSchema>

export const timelineSceneSourceStepSchema = z.object({
  storyId: z.string(),
  stepId: z.string(),
})
export type TimelineSceneSourceStep = z.infer<typeof timelineSceneSourceStepSchema>

export const timelineSceneSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  order: z.number().int().positive(),
  globalOrder: z.number().int().positive(),
  storyId: z.string(),
  stepId: z.string(),
  sourceSteps: z.array(timelineSceneSourceStepSchema).min(1),
  title: localizedTextSchema,
  narrative: z.object({
    setup: localizedTextSchema.optional(),
    development: localizedTextSchema.optional(),
    consequence: localizedTextSchema.optional(),
  }).refine((value) => value.setup || value.development || value.consequence, {
    message: 'Timeline scene narrative requires setup, development, or consequence',
  }).optional(),
  startTick: z.number().int().nonnegative(),
  endTick: z.number().int().nonnegative(),
  operationIds: z.array(z.string()),
  graphNodeIds: z.array(z.string()).default([]),
  graphEdgeIds: z.array(z.string()).default([]),
  primaryFocus: timelineTargetSchema,
  focusPath: z.array(timelineTargetSchema).max(4).default([]),
  chronologyNote: localizedTextSchema.optional(),
})
export type TimelineScene = z.infer<typeof timelineSceneSchema>

export const timelineCoverageStatusSchema = z.enum(['complete', 'partial', 'editorial-only'])
export type TimelineCoverageStatus = z.infer<typeof timelineCoverageStatusSchema>

export const timelineSubEraSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  order: z.number().int().positive(),
  title: localizedTextSchema,
  summary: localizedTextSchema,
  chronologyMode: z.enum(['sequence', 'parallel', 'variant']),
  coverageStatus: timelineCoverageStatusSchema,
  coverageNote: localizedTextSchema.optional(),
  ticks: z.array(timelineTickSchema),
  scenes: z.array(timelineSceneSchema),
})
export type TimelineSubEra = z.infer<typeof timelineSubEraSchema>

export const timelineEraSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  order: z.number().int().positive(),
  title: localizedTextSchema,
  summary: localizedTextSchema,
  subEras: z.array(timelineSubEraSchema).min(1),
})
export type TimelineEra = z.infer<typeof timelineEraSchema>

export const timelineCoverageDecisionSchema = z.object({
  target: timelineTargetSchema,
  decision: z.enum(['introduce', 'revisit', 'excluded-with-reason']),
  reason: localizedTextSchema.optional(),
  review: z.enum(['go', 'adjust', 'stop']),
})
export type TimelineCoverageDecision = z.infer<typeof timelineCoverageDecisionSchema>

export const timelineRouteSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  title: localizedTextSchema,
  description: localizedTextSchema,
  chronologyDisclaimer: localizedTextSchema,
  eras: z.array(timelineEraSchema).min(1),
  coverage: z.object({
    decisions: z.array(timelineCoverageDecisionSchema),
    availableStorySteps: z.number().int().nonnegative(),
    mappedStorySteps: z.number().int().nonnegative(),
    productionNodes: z.number().int().nonnegative(),
    productionEdges: z.number().int().nonnegative(),
  }),
})
export type TimelineRoute = z.infer<typeof timelineRouteSchema>

export const timelineChunkSchema = z.object({
  schemaVersion: z.literal(2),
  routes: z.array(timelineRouteSchema).min(1),
})
export type TimelineChunk = z.infer<typeof timelineChunkSchema>

export const manifestSchema = z.object({
  schemaVersion: z.number().int().positive(),
  dataset: z.string(),
  defaultLocale: localeSchema,
  files: z.object({
    nodes: z.string(),
    nodeMedia: z.string(),
    nodeMediaV2: z.string().optional(),
    profiles: z.string().optional(),
    storyAppearances: z.string().optional(),
    edges: z.string(),
    sources: z.string(),
    storyCycles: z.string(),
    collections: z.string().optional(),
    stories: z.array(z.string()),
  }),
})
export type Manifest = z.infer<typeof manifestSchema>

export interface MythDataset {
  manifest: Manifest
  nodes: GraphNode[]
  edges: GraphEdge[]
  sources: SourceRef[]
  storyCycles: StoryCycle[]
  stories: Story[]
  storyAppearances: StoryAppearance[]
  collections: Collection[]
}

export type RawMythDataset = Omit<MythDataset, 'collections'> & { collections?: Collection[] }

export const parseDataset = (raw: RawMythDataset): MythDataset => ({
  manifest: manifestSchema.parse(raw.manifest),
  nodes: z.array(graphNodeSchema).parse(raw.nodes),
  edges: z.array(graphEdgeSchema).parse(raw.edges),
  sources: z.array(sourceRefSchema).parse(raw.sources),
  storyCycles: z.array(storyCycleSchema).parse(raw.storyCycles),
  stories: z.array(storySchema).parse(raw.stories),
  storyAppearances: z.array(storyAppearanceSchema).parse(raw.storyAppearances),
  collections: z.array(collectionSchema).parse(raw.collections ?? []),
})

export function deriveEdgeContexts(dataset: MythDataset): MythDataset {
  const eventIds = new Set(dataset.nodes.filter((node) => node.type === 'event').map((node) => node.id))
  const mergeUnique = (left: string[], right: string[]) => [...new Set([...left, ...right])]
  return {
    ...dataset,
    edges: dataset.edges.map((edge) => {
      const derivedContexts: EdgeContext[] = dataset.stories.flatMap((story) => story.steps
        .filter((step) => step.edgeIds.includes(edge.id))
        .map((step) => ({
          storyIds: [story.id],
          eventIds: step.nodeIds.filter((id) => eventIds.has(id)),
          stepIds: [step.id],
          sourceRefs: [...edge.sourceRefs],
        })))
      const contexts = edge.contexts.map((context) => ({ ...context }))
      derivedContexts.forEach((derived) => {
        const existingIndex = contexts.findIndex((context) => (
          context.storyIds.some((id) => derived.storyIds.includes(id))
          && context.stepIds.some((id) => derived.stepIds.includes(id))
        ))
        if (existingIndex < 0) {
          contexts.push(derived)
          return
        }
        const existing = contexts[existingIndex]
        contexts[existingIndex] = {
          ...existing,
          storyIds: mergeUnique(existing.storyIds, derived.storyIds),
          eventIds: mergeUnique(existing.eventIds, derived.eventIds),
          stepIds: mergeUnique(existing.stepIds, derived.stepIds),
          sourceRefs: [...existing.sourceRefs],
        }
      })
      return { ...edge, contexts }
    }),
  }
}

export function validateReferences(dataset: MythDataset): string[] {
  const errors: string[] = []
  const unique = (label: string, ids: string[]) => {
    const seen = new Set<string>()
    ids.forEach((id) => {
      if (seen.has(id)) errors.push(`Duplicate ${label} id: ${id}`)
      seen.add(id)
    })
  }
  unique('node', dataset.nodes.map((item) => item.id))
  unique('edge', dataset.edges.map((item) => item.id))
  unique('source', dataset.sources.map((item) => item.id))
  unique('story', dataset.stories.map((item) => item.id))
  unique('collection', dataset.collections.map((item) => item.id))

  const nodeIds = new Set(dataset.nodes.map((item) => item.id))
  const edgeIds = new Set(dataset.edges.map((item) => item.id))
  const sourceIds = new Set(dataset.sources.map((item) => item.id))
  const storyIds = new Set(dataset.stories.map((item) => item.id))
  const cycleIds = new Set(dataset.storyCycles.map((item) => item.id))
  const stepIds = new Set(dataset.stories.flatMap((story) => story.steps.map((step) => step.id)))

  dataset.nodes.forEach((node) => node.sourceRefs.forEach((id) => {
    if (!sourceIds.has(id)) errors.push(`Node ${node.id} references missing source ${id}`)
  }))
  dataset.nodes.forEach((node) => node.profile?.factBlocks.forEach((fact) => fact.sourceRefs.forEach((id) => {
    if (!sourceIds.has(id)) errors.push(`Node ${node.id} fact ${fact.id} references missing source ${id}`)
  })))
  dataset.nodes.forEach((node) => {
    if (node.type === 'artifact' && !node.artifactProfile) errors.push(`Artifact ${node.id} has no artifactProfile`)
    if (node.type !== 'artifact' && node.artifactProfile) errors.push(`Non-artifact ${node.id} contains artifactProfile`)
    if (node.type === 'place' && !node.locationStatus) errors.push(`Place ${node.id} has no locationStatus`)
    if (node.type !== 'place' && node.locationStatus) errors.push(`Non-place ${node.id} contains locationStatus`)
  })
  dataset.edges.forEach((edge) => {
    if (!nodeIds.has(edge.source)) errors.push(`Edge ${edge.id} has missing source node ${edge.source}`)
    if (!nodeIds.has(edge.target)) errors.push(`Edge ${edge.id} has missing target node ${edge.target}`)
    edge.sourceRefs.forEach((id) => {
      if (!sourceIds.has(id)) errors.push(`Edge ${edge.id} references missing source ${id}`)
    })
    edge.storyRefs.forEach((id) => {
      if (!storyIds.has(id)) errors.push(`Edge ${edge.id} references missing story ${id}`)
    })
    const scopedStoryIds = new Set([
      ...edge.storyRefs,
      ...edge.contexts.flatMap((context) => context.storyIds),
    ])
    const scopedEventIds = new Set(edge.contexts.flatMap((context) => context.eventIds))
    const scopedStepIds = new Set(edge.contexts.flatMap((context) => context.stepIds))
    const hasVariant = edge.contexts.some((context) => Boolean(context.variantLabel) || context.relationshipClass === 'disputed')
    if (edge.relationshipScope === 'story' && scopedStoryIds.size === 0) {
      errors.push(`Story-scoped edge ${edge.id} has no story context`)
    }
    if (edge.relationshipScope === 'event' && (scopedStoryIds.size === 0 || (scopedEventIds.size === 0 && scopedStepIds.size === 0))) {
      errors.push(`Event-scoped edge ${edge.id} has no story plus event/step context`)
    }
    if (edge.relationshipScope === 'variant' && !hasVariant) {
      errors.push(`Variant-scoped edge ${edge.id} has no variant or disputed context`)
    }
    edge.contexts.forEach((context) => {
      if (Boolean(context.relationshipClass) !== Boolean(context.explanationDecision)) {
        errors.push(`Edge ${edge.id} context must pair relationshipClass with explanationDecision`)
      }
      if (context.explanationDecision === 'required' && !context.explanation) {
        errors.push(`Edge ${edge.id} required explanation is missing`)
      }
      if (context.explanationDecision === 'not-required' && (context.explanation || context.explanationGraph || context.deferredReason)) {
        errors.push(`Edge ${edge.id} not-required context contains explanation data`)
      }
      if (context.explanationDecision === 'deferred-with-reason' && !context.deferredReason) {
        errors.push(`Edge ${edge.id} deferred explanation has no reason`)
      }
      if (context.explanationGraph && !context.explanation) {
        errors.push(`Edge ${edge.id} explanation graph has no textual explanation`)
      }
      if (context.explanation && context.relationshipClass !== 'disputed' && context.explanation.kind !== context.relationshipClass) {
        errors.push(`Edge ${edge.id} explanation kind does not match relationship class`)
      }
      context.storyIds.forEach((id) => {
        if (!storyIds.has(id)) errors.push(`Edge ${edge.id} context references missing story ${id}`)
      })
      context.eventIds.forEach((id) => {
        if (!nodeIds.has(id)) errors.push(`Edge ${edge.id} context references missing event ${id}`)
      })
      context.stepIds.forEach((id) => {
        if (!stepIds.has(id)) errors.push(`Edge ${edge.id} context references missing step ${id}`)
      })
      context.sourceRefs.forEach((id) => {
        if (!sourceIds.has(id)) errors.push(`Edge ${edge.id} context references missing source ${id}`)
      })
      context.explanationGraph?.nodeIds.forEach((id) => {
        if (!nodeIds.has(id)) errors.push(`Edge ${edge.id} explanation graph references missing node ${id}`)
      })
      context.explanationGraph?.edgeIds.forEach((id) => {
        const graphEdge = dataset.edges.find((item) => item.id === id)
        if (!graphEdge) {
          errors.push(`Edge ${edge.id} explanation graph references missing edge ${id}`)
          return
        }
        if (!context.explanationGraph!.nodeIds.includes(graphEdge.source) || !context.explanationGraph!.nodeIds.includes(graphEdge.target)) {
          errors.push(`Edge ${edge.id} explanation graph omits an endpoint of ${id}`)
        }
      })
    })
  })

  const parentsByChild = new Map<string, Set<string>>()
  dataset.edges.forEach((edge) => {
    if (edge.type !== 'family' || !edge.directed || !['father of', 'mother of', 'parent of'].includes(edge.label.en)) return
    const parents = parentsByChild.get(edge.target) ?? new Set<string>()
    parents.add(edge.source)
    parentsByChild.set(edge.target, parents)
  })
  dataset.edges.forEach((edge) => {
    if (edge.type !== 'love') return
    const sourceParents = parentsByChild.get(edge.source)
    const targetParents = parentsByChild.get(edge.target)
    if (!sourceParents || !targetParents || ![...sourceParents].some((parentId) => targetParents.has(parentId))) return
    const hasFamilyPair = dataset.edges.some((candidate) => (
      candidate.type === 'family'
      && !candidate.directed
      && ((candidate.source === edge.source && candidate.target === edge.target)
        || (candidate.source === edge.target && candidate.target === edge.source))
    ))
    if (!hasFamilyPair) errors.push(`Love edge ${edge.id} connects known siblings without a separate family edge`)
  })

  dataset.stories.forEach((story) => {
    story.steps.forEach((step, index) => {
      if (step.order !== index + 1) errors.push(`Story ${story.id} has non-contiguous step order`)
      step.nodeIds.forEach((id) => {
        if (!nodeIds.has(id)) errors.push(`Story ${story.id} references missing node ${id}`)
      })
      step.edgeIds.forEach((id) => {
        if (!edgeIds.has(id)) errors.push(`Story ${story.id} references missing edge ${id}`)
      })
      step.sourceRefs.forEach((id) => {
        if (!sourceIds.has(id)) errors.push(`Story ${story.id} references missing source ${id}`)
      })
    })
  })
  dataset.storyCycles.forEach((cycle) => cycle.storyIds.forEach((id) => {
    if (!storyIds.has(id)) errors.push(`Cycle ${cycle.id} references missing story ${id}`)
  }))
  unique('story appearance', dataset.storyAppearances.map((item) => item.id))
  dataset.storyAppearances.forEach((appearance) => {
    if (!nodeIds.has(appearance.entityId)) errors.push(`Appearance ${appearance.id} references missing entity ${appearance.entityId}`)
    if (!storyIds.has(appearance.storyId)) errors.push(`Appearance ${appearance.id} references missing story ${appearance.storyId}`)
    appearance.stepIds.forEach((id) => {
      if (!stepIds.has(id)) errors.push(`Appearance ${appearance.id} references missing step ${id}`)
    })
    appearance.sourceRefs.forEach((id) => {
      if (!sourceIds.has(id)) errors.push(`Appearance ${appearance.id} references missing source ${id}`)
    })
    appearance.stepContexts.forEach((context) => {
      if (!appearance.stepIds.includes(context.stepId)) errors.push(`Appearance ${appearance.id} step context is outside its stepIds: ${context.stepId}`)
      context.sourceRefs.forEach((id) => {
        if (!sourceIds.has(id)) errors.push(`Appearance ${appearance.id} step context references missing source ${id}`)
      })
    })
  })
  dataset.collections.forEach((collection) => {
    if (nodeIds.has(collection.id)) errors.push(`Collection ${collection.id} collides with node id ${collection.id}`)
    if (storyIds.has(collection.id)) errors.push(`Collection ${collection.id} collides with story id ${collection.id}`)
    if (cycleIds.has(collection.id)) errors.push(`Collection ${collection.id} collides with cycle id ${collection.id}`)
    const variantIds = collection.variants.map((variant) => variant.id)
    unique(`collection ${collection.id} variant`, variantIds)
    if (!variantIds.includes(collection.defaultVariantId)) {
      errors.push(`Collection ${collection.id} default variant is missing: ${collection.defaultVariantId}`)
    }
    if (collection.status === 'available') {
      if (!collection.sourceRefs.length) errors.push(`Available collection ${collection.id} has no sources`)
      if (!collection.exclusions.length) errors.push(`Available collection ${collection.id} has no exclusions`)
      if (!collection.relatedStoryIds.length && !collection.relatedCycleIds.length) {
        errors.push(`Available collection ${collection.id} has no related story or cycle context`)
      }
      if (collection.review !== 'go') errors.push(`Available collection ${collection.id} is missing a go review decision`)
    }
    collection.sourceRefs.forEach((id) => {
      if (!sourceIds.has(id)) errors.push(`Collection ${collection.id} references missing source ${id}`)
    })
    collection.relatedStoryIds.forEach((id) => {
      if (!storyIds.has(id)) errors.push(`Collection ${collection.id} references missing story ${id}`)
    })
    collection.relatedCycleIds.forEach((id) => {
      if (!cycleIds.has(id)) errors.push(`Collection ${collection.id} references missing cycle ${id}`)
    })
    collection.variants.forEach((variant) => {
      if (collection.status === 'available' && variant.review !== 'go') {
        errors.push(`Available collection ${collection.id} variant ${variant.id} is missing a go review decision`)
      }
      variant.sourceRefs.forEach((id) => {
        if (!sourceIds.has(id)) errors.push(`Collection ${collection.id} variant ${variant.id} references missing source ${id}`)
      })
      const memberIds = variant.members.map((member) => member.nodeId)
      unique(`collection ${collection.id} variant ${variant.id} member`, memberIds)
      variant.members.forEach((member) => {
        if (!nodeIds.has(member.nodeId)) errors.push(`Collection ${collection.id} variant ${variant.id} references missing node ${member.nodeId}`)
        if (!variant.sourceRefs.length && !member.sourceRefs.length) {
          errors.push(`Collection ${collection.id} variant ${variant.id} member ${member.nodeId} has no source support`)
        }
        if (collection.status === 'available' && member.review !== 'go') {
          errors.push(`Available collection ${collection.id} variant ${variant.id} member ${member.nodeId} is missing a go review decision`)
        }
        member.sourceRefs.forEach((id) => {
          if (!sourceIds.has(id)) errors.push(`Collection ${collection.id} variant ${variant.id} member ${member.nodeId} references missing source ${id}`)
        })
      })
    })
  })
  return errors
}
